import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { signMediaUrls } from "../api";
import { queryKeys } from "../queryKeys";
import type { TimelineItem } from "./timeline";

/** 何件先まで先読みするか。指に追従して送れるようになったぶん、多めに前後3件を用意する */
export const PREFETCH_DISTANCE = 3;

/** 署名付きURLの有効期限は1時間。切れる手前までは取り直さない */
const URL_STALE_MS = 50 * 60 * 1000;

/**
 * 表示中メディアの原本の署名付きURL。
 * サムネイルと違い一覧では使わないため、まとめて発行せず都度署名する。
 */
export function useSourceUrl(media: TimelineItem | undefined) {
  return useQuery({
    queryKey: queryKeys.mediaUrl(media?.storage_key),
    queryFn: async () => {
      if (media === undefined) return null;
      const urls = await signMediaUrls([media.storage_key]);
      return urls[media.storage_key] ?? null;
    },
    enabled: media !== undefined,
    staleTime: URL_STALE_MS,
  });
}

/**
 * 送った先を先に用意しておく。
 *
 * 署名と実体の取得で2往復かかるため、送ってから始めると必ず待たされる。
 * 見ている間に隣を用意しておけば、送った時点で手元にある。
 *
 * 写真は実体まで温めるが、**動画は署名だけに留める**。
 * 数百MBになりうるものを、見るとは限らないまま通信で引くのは割に合わない。
 */
export function usePrefetchNeighbours(items: TimelineItem[], index: number) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const neighbours: TimelineItem[] = [];
    for (let offset = -PREFETCH_DISTANCE; offset <= PREFETCH_DISTANCE; offset += 1) {
      if (offset === 0) continue;
      const neighbour = items[index + offset];
      if (neighbour === undefined) continue;
      // 既に署名済みのものは頼まない
      if (queryClient.getQueryData(queryKeys.mediaUrl(neighbour.storage_key)) !== undefined) {
        continue;
      }
      neighbours.push(neighbour);
    }
    if (neighbours.length === 0) return;

    let cancelled = false;
    void signMediaUrls(neighbours.map((neighbour) => neighbour.storage_key))
      .then((urls) => {
        if (cancelled) return;
        for (const neighbour of neighbours) {
          const url = urls[neighbour.storage_key];
          if (url === undefined) continue;
          queryClient.setQueryData(queryKeys.mediaUrl(neighbour.storage_key), url);
          if (neighbour.type === "photo") {
            const image = new Image();
            image.src = url;
          }
        }
      })
      .catch(() => {
        // 先読みの失敗は黙って諦める。実際に送ったときに改めて取りに行く
      });

    return () => {
      cancelled = true;
    };
  }, [index, items, queryClient]);
}
